import { createPortal } from 'react-dom'
import { AlertTriangle } from 'lucide-react'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'

export interface CancelReimbursementConfirmModalProps {
  isOpen: boolean
  confirmValue: string
  onConfirmValueChange: (value: string) => void
  onClose: () => void
  onConfirm: () => void
}

export function CancelReimbursementConfirmModal({
  isOpen,
  confirmValue,
  onConfirmValueChange,
  onClose,
  onConfirm,
}: CancelReimbursementConfirmModalProps) {
  useBodyScrollLock(isOpen)
  const canConfirm = confirmValue.trim().toUpperCase() === 'DELETE'

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 p-4">
      <div
        className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cancel-reimbursement-title"
        data-testid="cancel-reimbursement-modal"
      >
        <div className="flex items-center gap-3 bg-red-600 px-5 py-4">
          <AlertTriangle size={22} className="text-white" strokeWidth={2.5} aria-hidden="true" />
          <h3 id="cancel-reimbursement-title" className="text-lg font-semibold text-white">
            Cancel Reimbursement Request
          </h3>
        </div>

        <div className="space-y-4 px-5 py-4">
          <p className="text-sm text-gray-700">
            This will permanently cancel this reimbursement request. The request will be marked as
            <span className="font-semibold"> Concluded — Cancelled</span> and cannot be reopened.
          </p>
          <div>
            <label htmlFor="cancel-reimbursement-confirm" className="mb-1.5 block text-sm font-medium text-gray-700">
              Type <span className="font-mono font-bold text-red-600">DELETE</span> to confirm
            </label>
            <input
              id="cancel-reimbursement-confirm"
              type="text"
              value={confirmValue}
              onChange={(e) => onConfirmValueChange(e.target.value.toUpperCase())}
              placeholder="DELETE"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm uppercase tracking-wide focus:border-transparent focus:ring-2 focus:ring-red-500"
              autoComplete="off"
              autoFocus
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!canConfirm}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Cancel Request
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
